// components/LeaveModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

const ApplyLeaveModal = ({
  isOpen,
  isClosing,
  showModalContent,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  selectedStaff,
  staffList,
  agent,
  setAgent,
  leaveType,
  setLeaveType,
  handleSubmit,
  handleClose,
  dayLeaveRecords,
  fetchDayLeaveRecords,
  fetchMyLeaveDates,
  fetchUsedLeaveCount,
  loadLeaveLimitsFromServer,
  refreshCalendar,
  canSubmit,        // ✅ 👈 加這行
  isAlreadyApplied,
  isAuthenticated, // 是否已登入
  isSubmitting,
}) => {
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const agentMenuRef = useRef(null);

  const agentOptions = useMemo(
    () => staffList.filter((s) => s.name && s.name !== selectedStaff),
    [staffList, selectedStaff]
  );

  useEffect(() => {
    if (!isOpen) {
      setAgentMenuOpen(false);
      return;
    }

    if (!agent) return;

    const isValidAgent = agentOptions.some((s) => s.name === agent);
    if (!isValidAgent) {
      setAgent('');
    }
  }, [agent, agentOptions, setAgent, isOpen]);

  useEffect(() => {
    if (!agentMenuOpen) return;

    const handleClickOutside = (event) => {
      if (agentMenuRef.current && !agentMenuRef.current.contains(event.target)) {
        setAgentMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [agentMenuOpen]);

  const handleDeleteLeave = async (id) => {
    if (!window.confirm("確定要刪除這筆請假紀錄？")) return;

    try {
      const res = await fetch(`/api/delete-leave-record/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`刪除失敗：${errText}`);
      }

      const result = await res.json();
      if (result.success) {
        // alert("✅ 已刪除");

        // 🔁 重新抓資料
        await fetchDayLeaveRecords(fromDate);     // 更新當日列表
        await fetchMyLeaveDates();                // 更新紅框
        await fetchUsedLeaveCount();              // 更新人數（會影響白宿顯示）
        if (refreshCalendar) refreshCalendar();
        // ✅ 若想立刻觸發日曆格子刷新「白1」，需多做以下其中一件事：

        // 方法一：強制切換月份再切回
        // setCurrentDate(prev => moment(prev).add(1, 'month'));
        // setTimeout(() => setCurrentDate(prev => moment(prev).subtract(1, 'month')), 100);

        // 方法二（推薦）：從父元件傳入 loadLeaveLimitsFromServer()
        if (loadLeaveLimitsFromServer) await loadLeaveLimitsFromServer();
        // ✅ 關閉 Modal
        handleClose();
      } else {
        alert("❌ 刪除失敗：" + (result.message || '未知錯誤'));
      }
    } catch (err) {
      console.error(err);
      alert("❌ 系統錯誤：" + err.message);
    }
  };


  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
      className={`relative transition-opacity duration-300 ease-in-out ${!showModalContent ? 'opacity-0' : 'opacity-100'
        } bg-white rounded-lg p-6 w-[calc(100%-1rem)] max-w-md max-h-[calc(100vh-1rem)] overflow-visible mx-auto mt-2 shadow-xl z-60 outline-none sm:w-[90%]`}
      overlayClassName={`fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start z-50 overflow-auto transition-opacity duration-300 ease-in-out ${isClosing ? 'opacity-0' : 'opacity-100'
        }`}
    >

      
      <div className="flex justify-end items-center mb-4">
        {/* <h3 className="text-xl font-bold text-teal-800">📝 請假申請</h3> */}
        <button onClick={handleClose} className="text-gray-500 hover:text-red-500 text-2xl font-semibold leading-none">
          關閉
        </button>
      </div>

      <div className="grid grid-cols-5 gap-2 items-center">
        <label className="col-span-1 font-semibold">起</label>
        <div className="col-span-4">
          <div className="w-40 border rounded p-2 bg-gray-100 text-gray-700">
            {fromDate ? moment(fromDate).format('YYYY-MM-DD HH:mm') : '尚未選擇'}
          </div>
        </div>

        <label className="col-span-1 font-semibold">迄</label>
        <div className="col-span-4">
          <div className="w-40 border rounded p-2 bg-gray-100 text-gray-700">
            {toDate ? moment(toDate).format('YYYY-MM-DD HH:mm') : '尚未選擇'}
          </div>
        </div>

        <label className="col-span-1 font-semibold">申請人</label>
        <div className="col-span-4 border p-2 rounded bg-gray-100">{selectedStaff}</div>

        <label className="col-span-1 font-semibold">代理人</label>
        <div className="col-span-4 relative z-30" ref={agentMenuRef}>
          <button
            type="button"
            onClick={() => setAgentMenuOpen((open) => !open)}
            className="w-full min-h-[44px] border rounded p-2 text-base bg-white text-left flex items-center justify-between gap-2 touch-manipulation"
            aria-haspopup="listbox"
            aria-expanded={agentMenuOpen}
          >
            <span className={agent ? 'text-gray-900' : 'text-gray-500'}>
              {agent || '請選擇代理人'}
            </span>
            <span className="text-gray-400 text-sm shrink-0">{agentMenuOpen ? '▲' : '▼'}</span>
          </button>
          {agentMenuOpen && (
            <ul
              role="listbox"
              className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto border rounded bg-white shadow-lg z-40"
            >
              {agentOptions.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">尚無可選代理人</li>
              ) : (
                agentOptions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={agent === s.name}
                      onClick={() => {
                        setAgent(s.name);
                        setAgentMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-3 text-base touch-manipulation hover:bg-teal-50 active:bg-teal-100 ${
                        agent === s.name ? 'bg-teal-100 font-semibold' : ''
                      }`}
                    >
                      {s.name}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <label className="col-span-1 font-semibold">假別</label>
        <select
          value={leaveType}
          onChange={(e) => setLeaveType(e.target.value)}
          className="col-span-4 w-full min-h-[44px] border rounded p-2 text-base bg-white"
        >
          <option value="加休">加休</option>
          <option value="補休">補休</option>
          <option value="休假">休假</option>
          <option value="外宿">外宿</option>
          <option value="公假">公假</option>
        </select>
      </div>

      <div className="text-center">
        <button
          onClick={handleSubmit}
          disabled={isAlreadyApplied || !canSubmit}
          className={`px-4 py-2 mt-2 rounded font-semibold shadow-md border
    ${isAlreadyApplied
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : !canSubmit
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-500 text-white hover:bg-teal-600'
            }`}
        >
          {isAlreadyApplied
            ? '你已申請'
            : !canSubmit
              ? '已滿，無法申請'
              : '送出申請'}
        </button>

        {/* <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`px-4 py-2 mt-2 rounded font-semibold shadow-md border 
    ${!canSubmit
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-teal-500 text-white hover:bg-teal-600'}`}
        >
          {!canSubmit ? '已滿，無法申請' : '送出申請'}
        </button> */}
      </div>

      {dayLeaveRecords.length > 0 && (

        <div className="mt-4 text-sm border-t pt-2 max-h-48 overflow-y-auto">
          <h4 className="font-semibold mb-1 text-gray-700 text-center">📋 本日請假調班紀錄</h4>
          <div className="space-y-1">
            {dayLeaveRecords.map((item, index) => (
              <div key={index} className="border rounded p-1 bg-gray-50 shadow-sm">
                {/* 第一列：起訖時間 */}
                <div className="text-[14px] text-gray-900 text-center font-bold">
                  {moment(item.fromDate).format('MM/DD HH:mm')} ~ {moment(item.toDate).format('MM/DD HH:mm')}
                </div>


                {/* 第二列：人名 + 班別 + 刪除 */}
                <div className="flex justify-between items-center text-[14px] ">
                  <div className="flex flex-col text-gray-700 leading-tight text-center font-bold w-full">
                    <div>{item.user}{item.leave_type || '未指定'}({item.agent}代理)</div>
                  </div>
                  {isAuthenticated && (
                    <button
                      onClick={() => handleDeleteLeave(item.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                      title="刪除紀錄"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}



{isSubmitting && (
  <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-50">
    <div className="animate-spin rounded-full h-14 w-14 border-4 border-teal-500 border-t-transparent"></div>
  </div>
)}


    </ReactModal>
  );
};
export default ApplyLeaveModal;
